import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActiveToast, IndividualConfig, ToastrService} from 'ngx-toastr';

export type NotificationLevel = 'success' | 'info' | 'warning' | 'error';

export interface NotificationOptions extends Partial<IndividualConfig> {
    title?: string;
}

@Injectable({providedIn: 'root'})
export class NotificationService {

    private static _instance: NotificationService | null = null;

    static get current(): NotificationService | null {
        return NotificationService._instance;
    }

    constructor(private readonly toastr: ToastrService) {
        NotificationService._instance = this;
    }

    success(message: unknown, options: NotificationOptions = {}): ActiveToast<unknown> {
        return this.show('success', message, options);
    }

    info(message: unknown, options: NotificationOptions = {}): ActiveToast<unknown> {
        return this.show('info', message, options);
    }

    warning(message: unknown, options: NotificationOptions = {}): ActiveToast<unknown> {
        return this.show('warning', message, options);
    }

    error(message: unknown, options: NotificationOptions = {}): ActiveToast<unknown> {
        return this.show('error', message, options);
    }

    private show(level: NotificationLevel, message: unknown, options: NotificationOptions): ActiveToast<unknown> {
        const text = NotificationService.formatMessage(message);
        const {title, ...config} = options;

        switch (level) {
            case 'success':
                return this.toastr.success(text, title, config);
            case 'info':
                return this.toastr.info(text, title, config);
            case 'warning':
                return this.toastr.warning(text, title, config);
            case 'error':
                return this.toastr.error(text, title ?? 'Error', config);
        }
    }

    private static formatMessage(value: unknown): string {
        if (value == null) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (value instanceof HttpErrorResponse) {
            const body: any = value.error;
            if (body && typeof body === 'object') {
                return body.message ?? body.title ?? body.detail ?? value.message ?? value.statusText;
            }
            if (typeof body === 'string' && body.length > 0) {
                return body;
            }
            return value.message ?? value.statusText ?? `HTTP ${value.status}`;
        }
        if (value instanceof Error) {
            return value.message;
        }
        if (typeof value === 'object') {
            const message = (value as any).message;
            if (typeof message === 'string') {
                return message;
            }
        }
        return String(value);
    }
}